import { usePermissions, useTranslate } from "ra-core";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type {
  PlayableKey,
  ProductMissionKey,
} from "../domain/training/missions";
import { missionViews, productMissionViews } from "../domain/training/progress";
import { GameRunner } from "./GameRunner";
import { MissionMap } from "./MissionMap";
import { PlayerHeader } from "./PlayerHeader";
import { STATIC_QUESTIONS } from "./content/product/staticQuestions";
import { ProductCourse } from "./ProductCourse";
import { useProductQuestions } from "./useProductQuestions";
import { TeamTab } from "./TeamTab";
import { useMyTraining } from "./useTraining";

type Tab = "me" | "team";

/**
 * /formation: the rep's level, XP and badges, then the mission map; a game opens in place of the map (the header
 * stays, so the XP moves as soon as a score is saved). Admins also get « Équipe ».
 */
export const FormationPage = () => {
  const t = useTranslate();
  const { permissions } = usePermissions();
  const isAdmin = permissions === "admin";
  const [tab, setTab] = useState<Tab>("me");
  const [playing, setPlaying] = useState<PlayableKey | null>(null);
  const [search, setSearch] = useSearchParams();
  const course: "phone" | "product" =
    search.get("parcours") === "produit" ? "product" : "phone";
  const q = useMyTraining();
  const views = useMemo(() => (q.data ? missionViews(q.data) : []), [q.data]);
  const productViews = useMemo(
    () => (q.data ? productMissionViews(q.data) : []),
    [q.data],
  );
  const questions = useProductQuestions();
  const changeCourse = (next: "phone" | "product") => {
    const params = new URLSearchParams(search);
    if (next === "product") params.set("parcours", "produit");
    else params.delete("parcours");
    setSearch(params, { replace: true });
    setPlaying(null);
  };
  const topRef = useRef<HTMLHeadingElement>(null);
  const effective: Tab = isAdmin && tab === "team" ? "team" : "me";

  // back to the map: return the focus to the page heading
  const wasPlaying = useRef(false);
  useEffect(() => {
    if (wasPlaying.current && playing === null) topRef.current?.focus();
    wasPlaying.current = playing !== null;
  }, [playing]);

  const body = () => {
    if (q.isPending) return <Skeleton className="h-64 w-full rounded-xl" />;
    if (q.isError || !q.data)
      return (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-2 text-sm text-destructive"
        >
          {t("gavion.training.load_error")}
          <Button size="sm" variant="outline" onClick={() => q.refetch()}>
            {t("gavion.training.retry")}
          </Button>
        </div>
      );
    const playingView = playing
      ? (course === "product" ? productViews : views).find(
          (v) => v.mission.key === playing,
        )
      : undefined;
    const onPlay = (k: PlayableKey) => {
      setPlaying(k);
      window.scrollTo?.({ top: 0 });
    };
    return (
      <>
        <PlayerHeader
          views={views}
          productViews={productViews}
          course={course}
        />
        {playing && playingView ? (
          <GameRunner
            key={playing}
            missionKey={playing}
            view={playingView}
            onExit={() => setPlaying(null)}
            {...(course === "product"
              ? {
                  // the catalog still loading: the content missions are playable from the approved questions
                  questions: (questions ?? STATIC_QUESTIONS)[
                    playing as ProductMissionKey
                  ],
                  explanationLink: {
                    to: "/formation/gavion",
                    label: t("gavion.training.product.read_context"),
                  },
                }
              : {})}
          />
        ) : course === "product" ? (
          <ProductCourse
            views={productViews}
            questions={questions}
            onPlay={onPlay}
          />
        ) : (
          <MissionMap views={views} onPlay={onPlay} />
        )}
      </>
    );
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 max-md:px-4 max-md:py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1
            ref={topRef}
            tabIndex={-1}
            className="text-2xl font-extrabold outline-none"
          >
            {t("gavion.training.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("gavion.training.subtitle")}
          </p>
          {/* GAVION gavion-context: the « Contexte Gavion », read only */}
          <Button asChild variant="link" className="h-auto px-0">
            <Link to="/formation/gavion">
              {t("gavion.training.gavion_page.link")}
            </Link>
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {effective === "me" && (
            <ToggleGroup
              type="single"
              variant="outline"
              value={course}
              onValueChange={(v) => v && changeCourse(v as "phone" | "product")}
              aria-label={t("gavion.training.product.course.label")}
            >
              <ToggleGroupItem value="phone" className="px-4">
                {t("gavion.training.product.course.phone")}
              </ToggleGroupItem>
              <ToggleGroupItem value="product" className="px-4">
                {t("gavion.training.product.course.product")}
              </ToggleGroupItem>
            </ToggleGroup>
          )}
          {isAdmin && (
            <ToggleGroup
              type="single"
              variant="outline"
              value={effective}
              onValueChange={(v) => v && setTab(v as Tab)}
              aria-label={t("gavion.training.tabs.label")}
            >
              <ToggleGroupItem value="me" className="px-4">
                {t("gavion.training.tabs.me")}
              </ToggleGroupItem>
              <ToggleGroupItem value="team" className="px-4">
                {t("gavion.training.tabs.team")}
              </ToggleGroupItem>
            </ToggleGroup>
          )}
        </div>
      </div>
      {effective === "team" ? <TeamTab /> : body()}
    </div>
  );
};
